/**
 * Shared contract between the Angular external-assistant route and the host embed SDK.
 * Query params are the internal transport; hosts should use the embed SDK script, not hand-built URLs.
 */

import {
  EmbedLauncherIconPreset,
  EmbedLauncherKind,
  EmbedPanelCorner
} from './external-assistant-embed.enums';

export {
  EMBED_CHILD_TO_PARENT_TYPE,
  EmbedChildToParentMessageAction,
  EmbedLauncherIconPreset,
  EmbedLauncherKind,
  EmbedPanelCorner,
  EmbedPanelMessageAction
} from './external-assistant-embed.enums';

/** @deprecated Use {@link EmbedPanelCorner} */
export type EmbedCorner = EmbedPanelCorner;

/** @deprecated Use {@link EmbedLauncherIconPreset} */
export type EmbedIconPreset = EmbedLauncherIconPreset;

/** postMessage envelope from parent (SDK) to iframe */
export const EMBED_POSTMESSAGE_TYPE = 'xa-ai-embed' as const;

export interface ExternalAssistantEmbedTheme {
  primaryColor: string;
  secondaryColor: string;
  borderRadius: string;
  /** Main conversation / panel inner surface (e.g. `#FFFFFF`). */
  backgroundColor: string;
  /** Primary text in the assistant body and composer (e.g. `#1A2233`). */
  textPrimaryColor: string;
  /** Muted text (timestamps, placeholders) when the embed overrides apply. */
  textSecondaryColor: string;
}

export interface ExternalAssistantEmbedLauncherChrome {
  label?: string;
  color?: string;
  iconUrl?: string;
  iconPreset?: EmbedLauncherIconPreset;
}

/** Parsed embed options (defaults applied). */
export interface ExternalAssistantEmbedOptions {
  launcher: boolean;
  launcherKind: EmbedLauncherKind;
  /** Initial and reset baseline for panel visibility when query is applied */
  defaultOpen: boolean;
  corner: EmbedPanelCorner;
  width: number;
  height: number;
  zIndex: number;
  /**
   * When true, the embed panel fills the viewport (CSS) and ignores {@link width}/{@link height} for layout.
   * Only enabled via the `panelFill` query flag (not implied by {@link launcher} being false).
   */
  panelFillViewport: boolean;
  theme: ExternalAssistantEmbedTheme;
  bubble: ExternalAssistantEmbedLauncherChrome;
  button: ExternalAssistantEmbedLauncherChrome;
  /** When absent, programmatic postMessage control is disabled */
  embedNonce: string | undefined;
  /**
   * When true, floating launcher UI is rendered by the host embed script (parent document), not inside the iframe.
   * Requires {@link embedParentOrigin} for iframe → parent `postMessage` targets.
   */
  externalLauncher: boolean;
  /** Browser origin of the host page embedding the iframe (used as `postMessage` target from the iframe). */
  embedParentOrigin: string | undefined;
}

/** Host / SDK input before serialization (partial). */
export type ExternalAssistantEmbedInitOptions = Partial<
  Pick<
    ExternalAssistantEmbedOptions,
    | 'launcher'
    | 'launcherKind'
    | 'defaultOpen'
    | 'corner'
    | 'width'
    | 'height'
    | 'zIndex'
    | 'panelFillViewport'
    | 'embedNonce'
  >
> & {
  theme?: Partial<ExternalAssistantEmbedTheme>;
  bubble?: ExternalAssistantEmbedLauncherChrome;
  button?: ExternalAssistantEmbedLauncherChrome;
  /** When true, serialized for SDK flows; launchers render in the parent page. */
  externalLauncher?: boolean;
  /** Host page origin (e.g. `https://shop.example.com`) for secure iframe → parent messaging. */
  embedParentOrigin?: string;
};

const QUERY = {
  launcher: 'launcher',
  launcherKind: 'launcherKind',
  defaultOpen: 'defaultOpen',
  corner: 'corner',
  width: 'width',
  height: 'height',
  zIndex: 'zIndex',
  embedNonce: 'embedNonce',
  themePrimary: 'themePrimary',
  themeSecondary: 'themeSecondary',
  themeRadius: 'themeRadius',
  themeBg: 'themeBg',
  themeTextPrimary: 'themeTextPrimary',
  themeTextSecondary: 'themeTextSecondary',
  bubbleLabel: 'bubbleLabel',
  bubbleColor: 'bubbleColor',
  bubbleIconUrl: 'bubbleIconUrl',
  bubbleIconPreset: 'bubbleIconPreset',
  buttonLabel: 'buttonLabel',
  buttonColor: 'buttonColor',
  buttonIconUrl: 'buttonIconUrl',
  buttonIconPreset: 'buttonIconPreset',
  externalLauncher: 'externalLauncher',
  embedParentOrigin: 'embedParentOrigin',
  panelFillViewport: 'panelFill'
} as const;

/** Portal default theme tokens used in embed samples and option parsing. */
export const DEFAULT_EMBED_THEME: Readonly<ExternalAssistantEmbedTheme> = {
  primaryColor: '#1B03A3',
  secondaryColor: '#4C6BD2',
  backgroundColor: '#F4F5FD',
  textPrimaryColor: '#ffffffd1',
  textSecondaryColor: '#1A2233',
  borderRadius: '16px'
};

const DEFAULT_THEME: ExternalAssistantEmbedTheme = { ...DEFAULT_EMBED_THEME };

const DEFAULTS: Omit<ExternalAssistantEmbedOptions, 'embedNonce'> = {
  launcher: true,
  launcherKind: EmbedLauncherKind.Bubble,
  defaultOpen: true,
  corner: EmbedPanelCorner.BottomRight,
  width: 420,
  height: 640,
  zIndex: 2147483645,
  panelFillViewport: false,
  theme: { ...DEFAULT_THEME },
  bubble: {},
  button: {},
  externalLauncher: false,
  embedParentOrigin: undefined
};

const CORNERS: ReadonlySet<string> = new Set<string>(Object.values(EmbedPanelCorner));
const LAUNCHER_KINDS: ReadonlySet<string> = new Set<string>(Object.values(EmbedLauncherKind));
const ICON_PRESETS: ReadonlySet<string> = new Set<string>(Object.values(EmbedLauncherIconPreset));

const MIN_DIM = 200;
const MAX_DIM = 900;
const MIN_Z = 1;
const MAX_Z = 2147483647;
const MAX_NONCE_LEN = 128;
const MAX_ICON_URL_LEN = 512;
const MAX_LABEL_LEN = 80;
const MAX_RADIUS_LEN = 24;

/**
 * Validates a host-supplied color token (hex, rgb, or hsl). Rejects arbitrary CSS.
 *
 * @param raw Raw string from query or SDK
 * @returns Normalized color or undefined if invalid
 */
export function validateEmbedColor(raw: string | undefined | null): string | undefined {
  if (raw == null) {
    return undefined;
  }
  const s = String(raw).trim();
  if (s.length === 0 || s.length > 64) {
    return undefined;
  }
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(s)) {
    return s;
  }
  const rgb = s.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*[\d.]+\s*)?\)$/i);
  if (rgb) {
    const [ , a, b, c ] = rgb;
    if ([ a, b, c ].every(n => Number(n) <= 255)) {
      return s;
    }
  }
  const hsl = s.match(/^hsla?\(\s*(\d{1,3})\s*,\s*(\d{1,3})%\s*,\s*(\d{1,3})%\s*(?:,\s*[\d.]+\s*)?\)$/i);
  if (hsl) {
    const h = Number(hsl[1]);
    if (h <= 360 && Number(hsl[2]) <= 100 && Number(hsl[3]) <= 100) {
      return s;
    }
  }
  return undefined;
}

/**
 * Validates border-radius token (bounded length and safe character subset).
 *
 * @param raw Raw string
 * @returns Sanitized radius or undefined
 */
export function validateEmbedBorderRadius(raw: string | undefined | null): string | undefined {
  if (raw == null) {
    return undefined;
  }
  const s = String(raw).trim();
  if (s.length === 0 || s.length > MAX_RADIUS_LEN) {
    return undefined;
  }
  if (!/^[\d.]+(px|rem|em|%)?$/.test(s) && s !== '0') {
    return undefined;
  }
  return s;
}

/**
 * Validates https icon URL for launcher chrome (length only; optional hostname allowlist is a future enhancement).
 *
 * @param raw URL string
 * @returns URL or undefined
 */
export function validateEmbedIconUrl(raw: string | undefined | null): string | undefined {
  if (raw == null) {
    return undefined;
  }
  const s = String(raw).trim();
  if (s.length === 0 || s.length > MAX_ICON_URL_LEN) {
    return undefined;
  }
  try {
    const u = new URL(s);
    if (u.protocol !== 'https:') {
      return undefined;
    }
    return u.toString();
  } catch {
    return undefined;
  }
}

/**
 * Clamps an integer to an inclusive range.
 *
 * @param n Input number
 * @param min Minimum allowed value
 * @param max Maximum allowed value
 */
function clampInt(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) {
    return min;
  }
  return Math.min(max, Math.max(min, Math.floor(n)));
}

/**
 * Parses a boolean query flag with fallback to a default.
 *
 * @param v Raw query value
 * @param defaultVal Value when missing or unrecognized
 */
function parseBool(v: string | null | undefined, defaultVal: boolean): boolean {
  if (v == null || v === '') {
    return defaultVal;
  }
  const t = v.trim().toLowerCase();
  if ([ '1', 'true', 'yes' ].includes(t)) {
    return true;
  }
  if ([ '0', 'false', 'no' ].includes(t)) {
    return false;
  }
  return defaultVal;
}

/**
 * Parses a bounded integer from a query string segment.
 *
 * @param v Raw query value
 * @param min Minimum (inclusive)
 * @param max Maximum (inclusive)
 * @param defaultVal Fallback when missing or invalid
 */
function parseIntParam(v: string | null | undefined, min: number, max: number, defaultVal: number): number {
  if (v == null || v === '') {
    return defaultVal;
  }
  const n = Number.parseInt(v, 10);
  if (Number.isNaN(n)) {
    return defaultVal;
  }
  return clampInt(n, min, max);
}

/**
 * @param params Parsed query map
 * @param key Query key
 * @returns True when the key is present with a non-empty value
 */
function hasNonEmptyQueryValue(params: URLSearchParams, key: string): boolean {
  const v = params.get(key);
  return v != null && String(v).trim() !== '';
}

/**
 * Parses panel/launcher corner from query.
 *
 * @param v Raw `corner` query value
 */
function parseCorner(v: string | null | undefined): EmbedPanelCorner {
  if (v && CORNERS.has(v)) {
    return v as EmbedPanelCorner;
  }
  return DEFAULTS.corner;
}

/**
 * Parses launcher kind when launcher is enabled.
 *
 * @param v Raw `launcherKind` query value
 */
function parseLauncherKind(v: string | null | undefined): EmbedLauncherKind {
  if (v && LAUNCHER_KINDS.has(v)) {
    return v as EmbedLauncherKind;
  }
  return DEFAULTS.launcherKind;
}

/**
 * Parses optional launcher icon preset.
 *
 * @param v Raw `bubbleIconPreset` / `buttonIconPreset` value
 */
function parseIconPreset(v: string | null | undefined): EmbedLauncherIconPreset | undefined {
  if (v == null || v === '') {
    return undefined;
  }
  if (ICON_PRESETS.has(v)) {
    return v as EmbedLauncherIconPreset;
  }
  return undefined;
}

/**
 * Parses and truncates a label segment for launcher chrome.
 *
 * @param v Raw label query value
 */
function parseLabel(v: string | null | undefined): string | undefined {
  if (v == null) {
    return undefined;
  }
  const s = String(v).trim().slice(0, MAX_LABEL_LEN);
  return s.length ? s : undefined;
}

/**
 * Parses embed nonce for postMessage trust channel.
 *
 * @param v Raw `embedNonce` query value
 */
function parseNonce(v: string | null | undefined): string | undefined {
  if (v == null || v === '') {
    return undefined;
  }
  const s = String(v).trim();
  if (s.length > MAX_NONCE_LEN || !/^[A-Za-z0-9._~-]+$/.test(s)) {
    return undefined;
  }
  return s;
}

/**
 * Parses the embedding host origin used as `postMessage` target from the iframe.
 *
 * @param raw Raw `embedParentOrigin` query value (may be percent-encoded)
 */
function parseEmbedParentOrigin(raw: string | null | undefined): string | undefined {
  if (raw == null || raw === '') {
    return undefined;
  }
  let s: string;
  try {
    s = decodeURIComponent(String(raw).trim());
  } catch {
    return undefined;
  }
  if (s.length === 0 || s.length > 512) {
    return undefined;
  }
  try {
    const u = new URL(s);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') {
      return undefined;
    }
    return u.origin;
  } catch {
    return undefined;
  }
}

/**
 * Parses bubble or button chrome keys from the query map.
 *
 * @param params Full query map
 * @param prefix Which launcher style block to read
 */
function parseChrome(params: URLSearchParams, prefix: 'bubble' | 'button'): ExternalAssistantEmbedLauncherChrome {
  const labelKey = prefix === 'bubble' ? QUERY.bubbleLabel : QUERY.buttonLabel;
  const colorKey = prefix === 'bubble' ? QUERY.bubbleColor : QUERY.buttonColor;
  const iconUrlKey = prefix === 'bubble' ? QUERY.bubbleIconUrl : QUERY.buttonIconUrl;
  const presetKey = prefix === 'bubble' ? QUERY.bubbleIconPreset : QUERY.buttonIconPreset;
  const rawLabel = parseLabel(params.get(labelKey));
  const color = validateEmbedColor(params.get(colorKey));
  const iconUrl = validateEmbedIconUrl(params.get(iconUrlKey));
  const iconPreset = parseIconPreset(params.get(presetKey));
  const out: ExternalAssistantEmbedLauncherChrome = {};
  if (rawLabel !== undefined) {
    out.label = rawLabel;
  }
  if (color !== undefined) {
    out.color = color;
  }
  if (iconUrl !== undefined) {
    out.iconUrl = iconUrl;
  }
  if (iconPreset !== undefined) {
    out.iconPreset = iconPreset;
  }
  return out;
}

/**
 * Parses whitelisted embed options from URL search params (e.g. iframe location).
 *
 * @param params Query string parameters
 * @returns Options with defaults for omitted keys
 */
export function parseEmbedOptionsFromSearchParams(params: URLSearchParams): ExternalAssistantEmbedOptions {
  const launcher = parseBool(params.get(QUERY.launcher), DEFAULTS.launcher);
  const rawExternalLauncher = parseBool(params.get(QUERY.externalLauncher), false);
  let externalLauncher = launcher && rawExternalLauncher;
  const embedParentOrigin = externalLauncher ? parseEmbedParentOrigin(params.get(QUERY.embedParentOrigin)) : undefined;
  if (externalLauncher && embedParentOrigin == null) {
    externalLauncher = false;
  }
  const panelFillViewport = parseBool(params.get(QUERY.panelFillViewport), DEFAULTS.panelFillViewport);
  const defaultBorderRadius = launcher ? DEFAULT_THEME.borderRadius : '0';
  const theme: ExternalAssistantEmbedTheme = {
    primaryColor: validateEmbedColor(params.get(QUERY.themePrimary)) ?? DEFAULT_THEME.primaryColor,
    secondaryColor: validateEmbedColor(params.get(QUERY.themeSecondary)) ?? DEFAULT_THEME.secondaryColor,
    borderRadius: validateEmbedBorderRadius(params.get(QUERY.themeRadius)) ?? defaultBorderRadius,
    backgroundColor: validateEmbedColor(params.get(QUERY.themeBg)) ?? DEFAULT_THEME.backgroundColor,
    textPrimaryColor: validateEmbedColor(params.get(QUERY.themeTextPrimary)) ?? DEFAULT_THEME.textPrimaryColor,
    textSecondaryColor: validateEmbedColor(params.get(QUERY.themeTextSecondary)) ?? DEFAULT_THEME.textSecondaryColor
  };
  return {
    launcher,
    launcherKind: parseLauncherKind(params.get(QUERY.launcherKind)),
    defaultOpen: parseBool(params.get(QUERY.defaultOpen), DEFAULTS.defaultOpen),
    corner: parseCorner(params.get(QUERY.corner)),
    width: parseIntParam(params.get(QUERY.width), MIN_DIM, MAX_DIM, DEFAULTS.width),
    height: parseIntParam(params.get(QUERY.height), MIN_DIM, MAX_DIM, DEFAULTS.height),
    zIndex: parseIntParam(params.get(QUERY.zIndex), MIN_Z, MAX_Z, DEFAULTS.zIndex),
    panelFillViewport,
    theme,
    bubble: parseChrome(params, 'bubble'),
    button: parseChrome(params, 'button'),
    embedNonce: parseNonce(params.get(QUERY.embedNonce)),
    externalLauncher,
    embedParentOrigin: externalLauncher ? embedParentOrigin : undefined
  };
}

/**
 * Writes a query key when the value is non-empty.
 *
 * @param sp Target map
 * @param key Query key
 * @param value Value to set
 */
function appendIfDefined(sp: URLSearchParams, key: string, value: string | undefined | null): void {
  if (value != null && value !== '') {
    sp.set(key, value);
  }
}

/**
 * Serializes embed options to URLSearchParams for the iframe src query string.
 * Omits keys that match defaults where safe to shorten URLs (always includes embedNonce when provided).
 *
 * @param options Partial init options plus required nonce for SDK flows
 */
export function serializeEmbedOptionsToSearchParams(
  options: ExternalAssistantEmbedInitOptions & { embedNonce?: string }
): URLSearchParams {
  const sp = new URLSearchParams();
  const launcher = options.launcher ?? DEFAULTS.launcher;
  const launcherKind = options.launcherKind ?? DEFAULTS.launcherKind;
  const defaultOpen = options.defaultOpen ?? DEFAULTS.defaultOpen;
  const corner = options.corner ?? DEFAULTS.corner;
  const width = options.width ?? DEFAULTS.width;
  const height = options.height ?? DEFAULTS.height;
  const zIndex = options.zIndex ?? DEFAULTS.zIndex;
  const defaultBorderRadius = launcher ? DEFAULT_THEME.borderRadius : '0';

  if (launcher !== DEFAULTS.launcher) {
    sp.set(QUERY.launcher, launcher ? '1' : '0');
  }
  if (launcher && launcherKind !== DEFAULTS.launcherKind) {
    sp.set(QUERY.launcherKind, launcherKind as string);
  }
  if (defaultOpen !== DEFAULTS.defaultOpen) {
    sp.set(QUERY.defaultOpen, defaultOpen ? '1' : '0');
  }
  if (corner !== DEFAULTS.corner) {
    sp.set(QUERY.corner, corner as string);
  }
  if (launcher) {
    if (width !== DEFAULTS.width) {
      sp.set(QUERY.width, String(clampInt(width, MIN_DIM, MAX_DIM)));
    }
    if (height !== DEFAULTS.height) {
      sp.set(QUERY.height, String(clampInt(height, MIN_DIM, MAX_DIM)));
    }
  } else {
    if (options.width != null) {
      sp.set(QUERY.width, String(clampInt(options.width, MIN_DIM, MAX_DIM)));
    }
    if (options.height != null) {
      sp.set(QUERY.height, String(clampInt(options.height, MIN_DIM, MAX_DIM)));
    }
  }
  if (zIndex !== DEFAULTS.zIndex) {
    sp.set(QUERY.zIndex, String(clampInt(zIndex, MIN_Z, MAX_Z)));
  }
  if (options.panelFillViewport === true) {
    sp.set(QUERY.panelFillViewport, '1');
  }

  const theme = options.theme ?? {};
  const tp = validateEmbedColor(theme.primaryColor);
  const ts = validateEmbedColor(theme.secondaryColor);
  const tr = validateEmbedBorderRadius(theme.borderRadius);
  const tbg = validateEmbedColor(theme.backgroundColor);
  const ttp = validateEmbedColor(theme.textPrimaryColor);
  const tts = validateEmbedColor(theme.textSecondaryColor);
  if (tp !== undefined && tp !== DEFAULT_THEME.primaryColor) {
    sp.set(QUERY.themePrimary, tp);
  }
  if (ts !== undefined && ts !== DEFAULT_THEME.secondaryColor) {
    sp.set(QUERY.themeSecondary, ts);
  }
  if (tr !== undefined && tr !== defaultBorderRadius) {
    sp.set(QUERY.themeRadius, tr);
  }
  if (tbg !== undefined && tbg !== DEFAULT_THEME.backgroundColor) {
    sp.set(QUERY.themeBg, tbg);
  }
  if (ttp !== undefined && ttp !== DEFAULT_THEME.textPrimaryColor) {
    sp.set(QUERY.themeTextPrimary, ttp);
  }
  if (tts !== undefined && tts !== DEFAULT_THEME.textSecondaryColor) {
    sp.set(QUERY.themeTextSecondary, tts);
  }

  /** Serializes bubble/button chrome into query keys. */
  function writeChrome(prefix: 'bubble' | 'button', chrome: ExternalAssistantEmbedLauncherChrome | undefined): void {
    if (!chrome) {
      return;
    }
    const labelKey = prefix === 'bubble' ? QUERY.bubbleLabel : QUERY.buttonLabel;
    const colorKey = prefix === 'bubble' ? QUERY.bubbleColor : QUERY.buttonColor;
    const iconUrlKey = prefix === 'bubble' ? QUERY.bubbleIconUrl : QUERY.buttonIconUrl;
    const presetKey = prefix === 'bubble' ? QUERY.bubbleIconPreset : QUERY.buttonIconPreset;
    const lab = parseLabel(chrome.label);
    const col = validateEmbedColor(chrome.color);
    const iu = validateEmbedIconUrl(chrome.iconUrl);
    const ip = chrome.iconPreset && ICON_PRESETS.has(chrome.iconPreset) ? chrome.iconPreset : undefined;
    appendIfDefined(sp, labelKey, lab);
    appendIfDefined(sp, colorKey, col);
    appendIfDefined(sp, iconUrlKey, iu);
    appendIfDefined(sp, presetKey, ip);
  }
  writeChrome('bubble', options.bubble);
  writeChrome('button', options.button);

  appendIfDefined(sp, QUERY.embedNonce, options.embedNonce != null ? parseNonce(options.embedNonce) ?? undefined : undefined);

  if (launcher && options.externalLauncher === true) {
    sp.set(QUERY.externalLauncher, '1');
  }
  const epo =
    options.embedParentOrigin != null ? parseEmbedParentOrigin(String(options.embedParentOrigin)) : undefined;
  appendIfDefined(sp, QUERY.embedParentOrigin, epo);

  return sp;
}
